import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DatePickerComponent, DropdownComponent, DropdownItem } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { adjustTime, HourToDayPartHourPipe, HourToDayPartPipe } from '@libs/utils';
import { END_HOUR, MAXIMUM_APPOINTMENT_LENGTH_IN_HOURS, ScheduleCalendarTime, START_HOUR } from '@libs/store';
import { DateTime } from 'luxon';
import { ConnectedPosition } from '@angular/cdk/overlay';

@Component({
  selector: 'lib-date-time-selector',
  imports: [CommonModule, DatePickerComponent, TranslateModule, DropdownComponent],
  providers: [HourToDayPartPipe, HourToDayPartHourPipe],
  templateUrl: './date-time-selector.component.html',
  styleUrl: './date-time-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateTimeSelectorComponent {
  protected readonly hourToDayPart = inject(HourToDayPartPipe);
  protected readonly hourToDayPartHour = inject(HourToDayPartHourPipe);

  @Input({ required: true }) public set appointmentDate(appointmentDate: DateTime) {
    this.appointmentDateSignal.set(appointmentDate);
  }
  @Input({ required: true }) public timeBlock!: number;
  @Input({ required: true }) public appointmentLength!: number;
  @Input({ required: true }) public set startTime(startTime: ScheduleCalendarTime) {
    this.startTimeCalendarTime.set(startTime);
    this.startTimeSignal.set(`${startTime.hour.toString().padStart(2, '0')}:${startTime.minute.toString().padStart(2, '0')}`);
  }
  @Input({ required: true }) public set endTime(endTime: ScheduleCalendarTime) {
    this.endTimeCalendarTime.set(endTime);
    this.endTimeSignal.set(`${endTime.hour.toString().padStart(2, '0')}:${endTime.minute.toString().padStart(2, '0')}`);
  }

  @Output() public dateSelected: EventEmitter<DateTime> = new EventEmitter<DateTime>();
  @Output() public startTimeSet: EventEmitter<ScheduleCalendarTime> = new EventEmitter<ScheduleCalendarTime>();
  @Output() public endTimeSet: EventEmitter<ScheduleCalendarTime> = new EventEmitter<ScheduleCalendarTime>();

  protected appointmentDateSignal = signal(DateTime.now());
  public appointmentDateFormattedSignal = computed(() => this.appointmentDateSignal().plus(0).toFormat('EEE, MMM d'));
  protected startTimeCalendarTime = signal<ScheduleCalendarTime>({ hour: 0, minute: 0 });
  protected endTimeCalendarTime = signal<ScheduleCalendarTime>({ hour: 0, minute: 0 });
  protected startTimeSignal = signal('');
  protected endTimeSignal = signal('');

  public timeOverlayHeight = '160px';
  public timeOverlayWidth = '270px';
  public overlayPositionStrategyFrom: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 10,
    },
  ];
  public overlayPositionStrategyTo: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 10,
    },
  ];

  public get startTimeStamps(): DropdownItem<string>[] {
    const timeStamps: DropdownItem<string>[] = [];
    const minimumHourFromEndTime = this.endTimeCalendarTime().hour - MAXIMUM_APPOINTMENT_LENGTH_IN_HOURS;
    const minimumHour = minimumHourFromEndTime < START_HOUR ? START_HOUR : minimumHourFromEndTime;
    for (let i = minimumHour; i < END_HOUR; i++) {
      const startingMinute =
        i === minimumHour && i !== START_HOUR
          ? adjustTime({ hour: this.endTimeCalendarTime().hour, minute: this.endTimeCalendarTime().minute }).minute
          : 0;
      for (let j = startingMinute; j < 60; j += this.timeBlock) {
        const hour = i.toString().padStart(2, '0');
        const minute = j.toString().padStart(2, '0');
        const hourToDisplay = this.hourToDayPartHour.transform(i);
        const dayPart = this.hourToDayPart.transform(i);
        timeStamps.push({
          value: `${hour}:${minute}`,
          label: `${hourToDisplay}:${minute} ${dayPart}`,
        });
      }
    }

    return timeStamps;
  }

  public get endTimeStamps(): DropdownItem<string>[] {
    // End time stamps must start 30 minutes after the start time and end at the end of the day or max 8 hours
    const startHour = this.startTimeCalendarTime().hour;
    const startMinute = this.startTimeCalendarTime().minute;
    const startTimeInMinutes = startHour * 60 + startMinute;
    const maxEndTimeInMinutes = Math.min(startTimeInMinutes + MAXIMUM_APPOINTMENT_LENGTH_IN_HOURS * 60, END_HOUR * 60);
    const numberOfTimeBlocks = (maxEndTimeInMinutes - (startTimeInMinutes + this.timeBlock)) / this.timeBlock + 1;

    return Array.from({ length: numberOfTimeBlocks }, (_, index) => {
      const timeInMinutes = startTimeInMinutes + this.timeBlock * (index + 1);
      const hour = Math.floor(timeInMinutes / 60)
        .toString()
        .padStart(2, '0');
      const minute = (timeInMinutes % 60).toString().padStart(2, '0');
      const hourToDisplay = this.hourToDayPartHour.transform(Math.floor(timeInMinutes / 60));
      const dayPart = this.hourToDayPart.transform(Math.floor(timeInMinutes / 60));
      return {
        value: `${hour}:${minute}`,
        label: `${hourToDisplay}:${minute} ${dayPart}`,
      };
    });
  }

  public onDateSelect(date: DateTime | null): void {
    this.dateSelected.emit(date ?? DateTime.now());
  }

  public onStartTimeSelect(startTime: string | string[] | null): void {
    if (!Array.isArray(startTime) && startTime) {
      const [hour, minute] = startTime.split(':').map((time) => Number.parseInt(time, 10));
      this.startTimeSet.emit({ hour, minute });
    }
  }

  public onEndTimeSelect(endTime: string | string[] | null): void {
    if (!Array.isArray(endTime) && endTime) {
      const [hour, minute] = endTime.split(':').map((time) => Number.parseInt(time, 10));
      this.endTimeSet.emit({ hour, minute });
    }
  }
}
